<template>
  <div class='h-100'>

    <base-map
      :map-center="map.center"
      :map-zoom="map.zoom"
      :vessels="mapVessels"
      :selected-vessel-imo="selectedVesselImo"
      :highlighted-vessel-imo="highlightedVesselImo"
      @vessel-popup-ready="handlePopupReady">

      <!-- map slot to l-map level -->
      <template #map-extras>
        <template v-if="vesselPoints.length">
          <l-circle-marker v-for="(point, index) in vesselPoints"
                    :key="`point-${index}`"
                    :radius="3"
                    :lat-lng="point.position">
            <l-tooltip :options="{permanent: false}">
              {{ point.timestamp }}
            </l-tooltip>
          </l-circle-marker>

          <l-polyline
            v-for="(line, lineIndex) in vesselTrackingLines"
            :key="`polyline-${lineIndex}`"
            :lat-lngs="line" />
        </template>
      </template>

    </base-map>
  </div>
</template>

<script>
import moment from 'moment'
import getNested from 'lodash-es/get'
import isEmpty from 'lodash-es/isEmpty'
import {
  LCircleMarker,
  LPolyline,
  LTooltip,
} from 'vue2-leaflet'
import { mapActions } from 'vuex';

import { getToday } from '@/shared/date-utils';
import { getVesselData } from '@/shared/api_formatters/vessels';
import { formatTrackingPoints } from '@/shared/coordinate-utils';
import BaseMap from '@/base_components/BaseMap';


export default {
  name: 'vessel-map',

  components: {
    BaseMap,
    LCircleMarker,
    LPolyline,
    LTooltip,
  },

  props: {
    vesselDetails: {
      type: Object,
      default: null,
    },

    startDate: {
      type: String,
      default: () => getToday().subtract(7, 'days').format('YYYY-MM-DD'),
    },

    endDate: {
      type: String,
      default: moment().format('YYYY-MM-DD'),
    },
  },

  data() {
    return {
      map: {
        zoom: 4,
        defaultVesselZoom: 8,
        center: [47.41322, -1.219482],
      },
      selectedVesselImo: null,
      highlightedVesselImo: null,
      vesselPoints: [],

    };
  },

  mounted() {
    this.handleVesselChanged();
  },

  methods: {
    ...mapActions('api/vessels', [
      'getVesselPositions',
    ]),

    handleMapReady(mapObject) {
      this.map = mapObject;
    },

    async handleVesselChanged() {
      this.setMapCenter();

      if(getNested(this.vesselDetails, 'imo')) {
        await this.loadVesselTracks();
      } else {
        this.resetMapData();
      }
    },

    setMapCenter() {
      const coordinates = this.getVesselCoords(this.vesselDetails);
      if(coordinates) {
        this.map.center = coordinates;
      }
      if(this.vesselDetailsAvailable) {
        this.map.zoom = this.map.defaultVesselZoom;
      }
    },

    async loadVesselTracks() {
      const data = {
        imo: this.vesselDetails.imo,
        start_datetime: `${this.startDate}T00:00:00`,
        end_datetime: `${this.endDate}T23:59:59`,
      };

      let positions = await this.getVesselPositions(data);
      this.vesselPoints = positions.map(
        point => ({
          timestamp: moment(point.timestamp).format('YYYY-MM-DD HH:mm:ss'),
          position: [point.lat, point.lon],
        })
      );
    },

    resetMapData() {
      this.selectedVesselImo = null;
      this.vesselPoints = [];
    },

    handlePopupReady(evt) {
      this.selectedVesselImo = getNested(evt, 'options.itemId', null);
      this.highlightedVesselImo = getNested(evt, 'options.itemId', null);
      evt._source.on('popupopen', this.handlePopupOpen);
      evt._source.on('popupclose', this.handlePopupClose);
      evt._source.openPopup();
    },

    handlePopupOpen(evt) {
      this.selectedVesselImo = getNested(evt, 'popup.options.itemId', null);
      this.highlightedVesselImo = getNested(evt, 'popup.options.itemId', null);
    },

    handlePopupClose() {
      this.selectedVesselImo = null;
      this.highlightedVesselImo = null;
    },

    getVesselCoords(vessel) {
      const coordinates = getNested(vessel, 'geometry.coordinates', null);
      if(coordinates) {
        return [coordinates[1], coordinates[0]];
      } else {
        return null;
      }
    },
  },

  computed: {
    vesselDetailsAvailable() {
      return !isEmpty(this.vesselDetails);
    },

    mapVessels() {
      if(this.vesselDetailsAvailable) {
        return [{...this.vesselDetails, popupData: this.vesselPopupData}];
      } else {
        return null;
      }
    },

    vesselPopupData() {
      if(this.vesselDetailsAvailable) {
        const popupData = getVesselData(this.vesselDetails);

        return {
          'IMO': popupData.imo,
          'Name': popupData.vesselName,
          'Segment': popupData.segment,
          'Laden status': popupData.ladenStatus,
          '% of max draught': popupData.ladenPercent,
          'Destination': popupData.destination,
          'Draught': popupData.draught,
          'Speed': popupData.speed,
          'Updated': popupData.lastUpdate,
          'ETA': popupData.eta,
        };
      } else {
        return {};
      }
    },

    vesselTrackingLines() {
      if(this.vesselPoints.length) {
        return formatTrackingPoints(this.vesselPoints);
      } else {
        return [];
      }
    },
  },

  watch: {
    async vesselDetails() {
      await this.handleVesselChanged();
    },
  },
};
</script>

<style scoped>
.map-container >>> .vue2leaflet-map {
  position: absolute;
  top: 0;
  left: 0;
}

.map-container >>> .vessel-highlight {
  z-index: 3000 !important;
}

.map-container >>> .vessel-icon {
  z-index: 3000 !important;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
}

.map-container >>> .vessel-icon-arrow {
  width: 24px;
  height: 24px;
}

.map-container >>>  .vessel-icon-circle {
  width: 10px;
  height: 10px;
}

.map-container >>>  .leaflet-tooltip {
  font-size: 14px;
  font-weight: 800;
}

</style>
